/**
 * Payment Categories Constants
 * Per Section 14.1: Each domain SHOULD have its own constants file
 */

/** Maximum depth allowed for the category tree */
export const MAX_CATEGORY_DEPTH = 3;

/** Pending change types for batch operations */
export enum PendingChangeType {
	CREATE = 'create',
	UPDATE = 'update',
	TOGGLE = 'toggle',
}

/** Pending status for visual indicators */
export enum PendingStatus {
	NEW = 'new',
	MODIFIED = 'modified',
}

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Type for pending change (using enum values from constants)
 */
export type PendingChangeTypeValue = `${PendingChangeType}`;

/**
 * Type for pending status (using enum values from constants, or null)
 */
export type PendingStatusType = `${PendingStatus}` | null;

/**
 * Payment Category interface representing a node in the category tree
 * Translations are key-based using pattern: payment_category.name.{category_name}
 */
export interface IPaymentCategory {
	id: number;
	name: string; // English snake_case identifier (e.g., "airline_tickets_purchase_abroad")
	parentId: number | null;
	isActive?: boolean;
	allowChildren?: boolean; // Whether child categories can be added to this category
	level?: number;
	sortOrder?: number;
	description?: string;
	paymentPurposeCodeIds?: number[]; // Links to payment purpose codes (many-to-many, leaf categories only)
	categoryPurposeCodes?: Array<{ paymentPurposeCodeId: number }>; // Junction table data from API
	children?: IPaymentCategory[];
	// Translation fields (fetched from content_translations table)
	nameEn?: string;
	nameHe?: string;
	disclaimerEn?: string;
	disclaimerHe?: string;
}

/**
 * Form data for creating/updating a payment category with translations
 * Note: name must be English snake_case as it's used for translation key lookup
 */
export interface IPaymentCategoryFormData {
	name: string; // English snake_case identifier (e.g., "airline_tickets_purchase_abroad")
	parentId?: number | null;
	description?: string;
	nameEn?: string; // Display name in English
	nameHe?: string; // Display name in Hebrew
	disclaimerEn?: string;
	disclaimerHe?: string;
	isActive?: boolean;
	paymentPurposeCodeIds?: number[]; // Links to payment purpose codes (many-to-many, leaf categories only)
}

/**
 * Props for the PaymentCategoryTree component
 */
export interface IPaymentCategoryTreeProps {
	categories: IPaymentCategory[];
	isLoading?: boolean;
	onToggleActive?: (categoryId: number, isActive: boolean) => void;
	// Batch save props
	hasPendingChanges?: boolean;
	pendingChangesCount?: number;
	pendingChanges?: IPendingChange[];
	onSave?: () => void;
	onCancelChanges?: () => void;
	getPendingStatus?: (categoryId: number) => PendingStatusType;
}

/**
 * Props for the PaymentCategoryNode component
 */
export interface IPaymentCategoryNodeProps {
	category: IPaymentCategory;
	depth: number;
	expandedIds: Record<number, boolean>;
	onToggleExpand: (id: number) => void;
	onEdit: (category: IPaymentCategory) => void;
	onAdd: (parentId: number) => void;
	onToggleActive?: (category: IPaymentCategory) => void;
	pendingStatus?: PendingStatusType;
	getPendingStatus?: (categoryId: number) => PendingStatusType;
	/** Whether the parent category is active (for visual inheritance) */
	parentIsActive?: boolean;
}

/**
 * Represents a pending change to be committed
 */
export interface IPendingChange {
	type: PendingChangeTypeValue;
	categoryId: number;
	parentId?: number | null;
	name?: string;
	isActive?: boolean;
	originalValue?: IPaymentCategory;
}
